/**
 * The funding products Tirhelo offers, and exactly which fields each one captures.
 *
 * This is the single description of a funding product: the application wizard reads it to decide
 * which inputs to render at origination, and the application details page reads the same list to
 * play those values back, so what a reviewer sees is always what was actually captured.
 */

export type FundingFieldKind = 'text' | 'amount' | 'date' | 'number';

export interface FundingFieldSpec {
  /** Field on LoanDetails that holds the value. */
  name: string;
  label: string;
  kind: FundingFieldKind;
  placeholder?: string;
  hint?: string;
}

/**
 * A document a funding product asks for. `type` is the document type it is stored under, so the
 * documents step can offer an upload slot for it and tell whether it has been supplied.
 */
export interface ProductDocumentSpec {
  type: string;
  label: string;
  /** False for what the guideline marks "where applicable / relevant / available". */
  required: boolean;
}

export interface FundingProductSpec {
  heading: string;
  shortName: string;
  blurb: string;
  /** Exactly the fields the product design shows, in design order. */
  fields: FundingFieldSpec[];
  /**
   * Transaction-specific documents this product requires, per the business's document guideline.
   * Shown as a checklist at origination and as upload slots on the documents step.
   */
  documents: ProductDocumentSpec[];
}

/**
 * The product designs all share one grid rhythm: two fields, one field, two full-width fields,
 * then two fields and a trailing one. Column spans are positional so every product page lines up.
 */
export const FUNDING_PRODUCT_COLUMN_SPANS = [6, 6, 6, 12, 12, 6, 6, 6];

export const FUNDING_PRODUCTS: Record<string, FundingProductSpec> = {
  PURCHASE_ORDER_FINANCE: {
    heading: 'PO & supplier details',
    shortName: 'PO Funding',
    blurb: 'Capture the purchase order, the full cost to fulfil it and the expected payment event.',
    fields: [
      { name: 'projectValue', label: 'Purchase order value', kind: 'amount' },
      { name: 'poCostToFulfil', label: 'Cost to fulfil the PO', kind: 'amount' },
      { name: 'customerContribution', label: 'Customer contribution', kind: 'amount', hint: 'Where applicable' },
      { name: 'supplierName', label: 'Supplier', kind: 'text', placeholder: 'Who the funds will be paid to' },
      { name: 'projectClientName', label: 'End customer / PO issuer', kind: 'text', placeholder: 'Who issued the purchase order' },
      { name: 'projectExpectedCompletionDate', label: 'Expected delivery date', kind: 'date' },
      { name: 'projectExpectedPaymentDate', label: 'Expected payment date', kind: 'date' },
      { name: 'projectReferenceNumber', label: 'PO / contract number', kind: 'text' }
    ],
    // From the business Documents Guide, 20 Oct 2026 (finding #7): "Always" = required
    documents: [
      { type: 'PURCHASE_ORDER', label: 'Purchase order or award', required: true },
      { type: 'SUPPLIER_QUOTATION', label: 'Supplier quotations or pro forma invoices', required: true },
      // Optional after the business test of 22 Oct 2026
      { type: 'BILL_OF_QUANTITIES', label: 'Pricing schedule / Bill of quantities', required: false },
      { type: 'PROGRAMME_OF_WORKS', label: 'Scope / specifications and delivery timeline', required: false },
      { type: 'CUSTOMER_CONTRIBUTION_EVIDENCE', label: 'Proof of client contribution (if the client contributes)', required: false },
      { type: 'CSD_REPORT', label: 'CSD report (where relevant)', required: false },
      { type: 'PAYMENT_CERTIFICATES', label: 'Prior invoices or payment evidence (to verify past performance)', required: false },
      { type: 'PROJECT_SITE_EVIDENCE', label: 'Project or site evidence (where relevant)', required: false }
    ]
  },

  INVOICE_DISCOUNTING_FINANCE: {
    heading: 'Invoice & debtor details',
    shortName: 'Invoice Discounting',
    blurb: 'Capture the completed invoice and confirm exactly when and by whom it will be paid.',
    fields: [
      { name: 'invoiceAmount', label: 'Invoice amount', kind: 'amount' },
      { name: 'invoiceDate', label: 'Invoice date', kind: 'date' },
      { name: 'invoiceDebtorName', label: 'Debtor / customer', kind: 'text', placeholder: 'Who owes the invoice' },
      { name: 'invoicePaymentTerms', label: 'Payment terms', kind: 'text', placeholder: 'e.g. 30 days' },
      { name: 'invoiceExpectedPaymentDate', label: 'Expected payment date', kind: 'date' },
      { name: 'invoiceAmountRequested', label: 'Amount requested against invoice', kind: 'amount' },
      { name: 'invoiceGoodsDescription', label: 'Goods / services delivered', kind: 'text' },
      { name: 'invoiceStatus', label: 'Invoice status', kind: 'text', placeholder: 'e.g. Submitted and undisputed' }
    ],
    // From the business Documents Guide, 20 Oct 2026 (finding #7): "Always" = required
    documents: [
      { type: 'INVOICE', label: 'Invoice to be funded', required: true },
      { type: 'PROOF_OF_CONTRACT', label: 'Underlying purchase order, contract or appointment', required: true },
      { type: 'PROOF_OF_DELIVERY', label: 'Proof of delivery or work completion', required: true },
      { type: 'DEBTOR_STATEMENT', label: 'Statement of account or payment history with the debtor', required: true },
      { type: 'EXPECTED_PAYMENT_CONFIRMATION', label: 'Evidence of expected payment date', required: true },
      { type: 'DEBTOR_CONFIRMATION', label: 'Debtor acceptance or certification (where available)', required: false }
    ]
  },

  WORKING_CAPITAL_FINANCE: {
    heading: 'Working capital details',
    shortName: 'Working Capital',
    blurb: 'Capture the short-term operating need and show which normal business cash flow will repay it.',
    fields: [
      { name: 'loanPurpose', label: 'Purpose of working capital', kind: 'text' },
      { name: 'workingCapitalMonthlyTurnover', label: 'Average monthly turnover', kind: 'amount' },
      { name: 'loanAmount', label: 'Amount required', kind: 'amount' },
      { name: 'primaryRepaymentSource', label: 'Repayment source', kind: 'text', placeholder: 'e.g. Normal customer receipts' },
      { name: 'workingCapitalOperatingCycleDays', label: 'Operating cycle (days)', kind: 'number' },
      { name: 'proposedRepaymentDate', label: 'Expected repayment date', kind: 'date' },
      { name: 'workingCapitalCurrentDebtors', label: 'Current debtors', kind: 'amount' },
      { name: 'workingCapitalCurrentCreditors', label: 'Current creditors', kind: 'amount' }
    ],
    // From the business Documents Guide, 20 Oct 2026 (finding #7): "Always" = required
    documents: [
      { type: 'COST_BREAKDOWN', label: 'Detailed use of funds breakdown', required: true },
      { type: 'CASH_FLOW_FORECAST', label: 'Cash flow projection', required: true },
      { type: 'EXPECTED_FUNDS_EVIDENCE', label: 'Evidence of expected inflows', required: true },
      { type: 'FINANCIAL_STATEMENTS', label: 'Financial statements or management information', required: true },
      { type: 'REPAYMENT_SOURCE_EVIDENCE', label: 'Contracts, orders or invoices supporting repayment (where applicable)', required: false },
      { type: 'SUPPLIER_QUOTATION', label: 'Supplier or expense quotations (where relevant)', required: false }
    ]
  },

  PROJECT_FINANCE: {
    heading: 'Project & contract details',
    shortName: 'Project Finance',
    blurb: 'Capture the project value, delivery stages, total budget and certified repayment milestones.',
    fields: [
      { name: 'projectValue', label: 'Contract / project value', kind: 'amount' },
      { name: 'projectTotalCost', label: 'Total project cost', kind: 'amount' },
      { name: 'customerContribution', label: 'Customer contribution', kind: 'amount', hint: 'Where applicable' },
      { name: 'projectClientName', label: 'Project owner', kind: 'text' },
      { name: 'projectStartDate', label: 'Project start date', kind: 'date' },
      { name: 'projectExpectedCompletionDate', label: 'Expected completion date', kind: 'date' },
      { name: 'projectPaymentMilestones', label: 'Payment milestones', kind: 'text', placeholder: 'e.g. 3 certified milestones' },
      { name: 'projectExpectedFirstPaymentDate', label: 'Expected first payment', kind: 'date' }
    ],
    // From the business Documents Guide, 20 Oct 2026 (finding #7): "Always" = required
    documents: [
      { type: 'AWARD_LETTER', label: 'Signed contract, appointment or award', required: true },
      { type: 'BILL_OF_QUANTITIES', label: 'BOQ or detailed costing', required: true },
      { type: 'PROGRAMME_OF_WORKS', label: 'Scope of works and programme or timeline', required: true },
      { type: 'SUPPLIER_QUOTATION', label: 'Supplier quotations', required: true },
      { type: 'PAYMENT_CERTIFICATES', label: 'Certificates or invoices already submitted (if work has started)', required: false },
      { type: 'PROOF_OF_DELIVERY', label: 'Proof of work completed (if work has started)', required: false },
      { type: 'PROJECT_SITE_EVIDENCE', label: 'Recent site or project photos (where site work exists)', required: false },
      { type: 'CSD_REPORT', label: 'CSD report (government projects)', required: false }
    ]
  },

  ASSET_FINANCE: {
    heading: 'Asset & supplier details',
    shortName: 'Asset Funding',
    blurb: 'Capture the asset, supplier and the income the asset will help the business generate.',
    fields: [
      { name: 'assetDescription', label: 'Asset description', kind: 'text', placeholder: 'e.g. 2024 Ford Ranger 2.0 Bi-Turbo' },
      { name: 'assetPurchasePrice', label: 'Purchase price', kind: 'amount' },
      { name: 'customerContribution', label: 'Customer deposit', kind: 'amount' },
      { name: 'supplierName', label: 'Supplier / dealer', kind: 'text' },
      { name: 'assetPurpose', label: 'Purpose of asset', kind: 'text', placeholder: 'What the asset will be used for' },
      { name: 'assetExpectedMonthlyIncome', label: 'Expected monthly income', kind: 'amount' },
      { name: 'assetUsefulLifeMonths', label: 'Useful life (months)', kind: 'number' },
      { name: 'assetInsuranceStatus', label: 'Insurance status', kind: 'text', placeholder: 'e.g. Quotation received' }
    ],
    // Asset funding is not covered by the document guideline; these are the product design's list.
    // From the business Documents Guide, 20 Oct 2026 (finding #7): "Always" = required
    // NaTIS / title / ownership evidence and photos come under the Security section when the asset is security
    documents: [
      { type: 'SUPPLIER_QUOTATION', label: 'Supplier quotation or pro forma invoice', required: true },
      { type: 'CUSTOMER_CONTRIBUTION_EVIDENCE', label: 'Proof of deposit or contribution (if applicable)', required: false },
      { type: 'PROOF_OF_CONTRACT', label: 'Linked contract or revenue evidence (where relevant)', required: false }
    ]
  },

  BRIDGING_FINANCE: {
    heading: 'Bridge & payer details',
    shortName: 'Bridge Funding',
    blurb: 'Capture the underlying transaction, the incoming payment and why the funding gap exists.',
    fields: [
      { name: 'loanAmount', label: 'Amount required', kind: 'amount' },
      { name: 'bridgeExpectedIncomingAmount', label: 'Expected incoming amount', kind: 'amount' },
      { name: 'proposedRepaymentDate', label: 'Requested repayment date', kind: 'date' },
      { name: 'bridgeUnderlyingTransaction', label: 'Underlying transaction / contract', kind: 'text' },
      { name: 'bridgeSourceOfExpectedPayment', label: 'Source of expected payment', kind: 'text' },
      { name: 'bridgePayerName', label: 'Payer / end customer', kind: 'text' },
      { name: 'bridgeExpectedPaymentDate', label: 'Expected payment date', kind: 'date' },
      { name: 'bridgeReasonForFundingGap', label: 'Reason for funding gap', kind: 'text' }
    ],
    // From the business Documents Guide, 20 Oct 2026 (finding #7): "Always" = required
    // The guide treats bridging funding with working capital: the same documents
    documents: [
      { type: 'COST_BREAKDOWN', label: 'Detailed use of funds breakdown', required: true },
      { type: 'CASH_FLOW_FORECAST', label: 'Cash flow projection', required: true },
      { type: 'EXPECTED_FUNDS_EVIDENCE', label: 'Evidence of expected inflows', required: true },
      { type: 'FINANCIAL_STATEMENTS', label: 'Financial statements or management information', required: true },
      { type: 'REPAYMENT_SOURCE_EVIDENCE', label: 'Contracts, orders or invoices supporting repayment (where applicable)', required: false },
      { type: 'SUPPLIER_QUOTATION', label: 'Supplier or expense quotations (where relevant)', required: false }
    ]
  }
};

// Operational cost finance is the older name for the same short-term operating need.
FUNDING_PRODUCTS.OPERATIONAL_COST_FINANCE = FUNDING_PRODUCTS.WORKING_CAPITAL_FINANCE;

export const getFundingProduct = (fundingType?: string): FundingProductSpec | undefined =>
  fundingType ? FUNDING_PRODUCTS[fundingType] : undefined;

/**
 * Display name for a funding product. Falls back to a readable version of the stored value so a
 * product added to the API before it is described here still reads sensibly.
 */
export const getFundingProductName = (fundingType?: string): string => {
  if (!fundingType) return '';
  const product = FUNDING_PRODUCTS[fundingType];
  if (product) return product.shortName;
  return fundingType
    .replace(/_/g, ' ')
    .toLowerCase()
    .replace(/\b\w/g, (letter) => letter.toUpperCase());
};
